/**
 * Product Master · Markets tab — per-market coverage editor.
 *
 * ONE product record; market availability is a relation
 * (pureh_product_markets). Default = available in every active market.
 * Currency is auto-derived from each market and never editable here.
 */
import { useCallback, useEffect, useState } from "react";
import { Globe, Save, RefreshCcw, Calculator } from "lucide-react";
import { Button, EmptyState } from "../../components/lib";
import { productMaster, PMMarketCoverage, PMCogs } from "../../services/api";

const LAUNCH_OPTIONS = [
  { value: "pre_launch",   label: "Pre-launch" },
  { value: "live",         label: "Live" },
  { value: "paused",       label: "Paused" },
  { value: "discontinued", label: "Discontinued" },
];
const FULFILLMENT_OPTIONS = [
  { value: "",                label: "—" },
  { value: "local_warehouse", label: "Local warehouse" },
  { value: "3pl",             label: "3PL" },
  { value: "cross_border",    label: "Cross-border" },
  { value: "dropship",        label: "Dropship" },
];

type Dirty = Record<string, Record<string, any>>;

export default function MarketCoveragePanel({ ventureId, productId }: {
  ventureId: string; productId: string;
}) {
  const [rows, setRows] = useState<PMMarketCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState<Dirty>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cogs, setCogs] = useState<Record<string, PMCogs>>({});
  const [bottleMl, setBottleMl] = useState(100);

  const loadCogs = useCallback(async (marketIds: string[], ml: number) => {
    const entries = await Promise.all(marketIds.map(async (mid) => {
      try { return [mid, await productMaster.cogs(ventureId, productId, mid, ml)] as const; }
      catch { return [mid, null] as const; }
    }));
    setCogs(Object.fromEntries(entries.filter(([, v]) => v)) as Record<string, PMCogs>);
  }, [ventureId, productId]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await productMaster.markets(ventureId, productId);
      setRows(r.coverage); setDirty({});
      loadCogs(r.coverage.map((c) => c.marketId), bottleMl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load market coverage.");
    } finally {
      setLoading(false);
    }
  }, [ventureId, productId, loadCogs, bottleMl]);
  useEffect(() => { load(); }, [load]);

  const val = (r: PMMarketCoverage, field: string): any =>
    dirty[r.marketId]?.[field] !== undefined ? dirty[r.marketId][field] : (r as any)[field];

  const edit = (marketId: string, field: string, v: any) =>
    setDirty((d) => ({ ...d, [marketId]: { ...(d[marketId] ?? {}), [field]: v } }));

  const saveRow = async (marketId: string, extra?: Record<string, any>) => {
    const patch = { ...(dirty[marketId] ?? {}), ...(extra ?? {}) };
    if (!Object.keys(patch).length) return;
    if (patch.price !== undefined && patch.price !== null) patch.price = Number(patch.price);
    if (patch.stock !== undefined && patch.stock !== null) patch.stock = Number(patch.stock);
    setSaving(marketId); setErr(null);
    try {
      const updated = await productMaster.patchMarket(ventureId, productId, marketId, patch);
      setRows((rs) => rs.map((r) => (r.marketId === marketId ? updated : r)));
      setDirty((d) => { const n = { ...d }; delete n[marketId]; return n; });
      loadCogs([marketId], bottleMl).catch(() => {});
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(null);
    }
  };

  const money = (v: number | null | undefined, cur: string | null) =>
    v === null || v === undefined ? "—" : `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${cur ?? ""}`;

  if (loading) return <div className="text-sm text-slate-500 py-6" data-testid="markets-loading">Loading market coverage…</div>;
  if (!rows.length) {
    return <EmptyState icon={Globe} title="No active markets" body="Add a market in system settings to define per-market coverage." testId="markets-empty" />;
  }

  return (
    <div data-testid="market-coverage-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">
          Products default to <b>available in every active market</b>. Toggle off to exclude.
          Price currency follows each market automatically.
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            Bottle
            <input type="number" min={1} value={bottleMl}
              onChange={(e) => setBottleMl(Math.max(1, Number(e.target.value) || 100))}
              data-testid="cogs-bottle-ml"
              className="w-16 px-2 py-1 text-xs border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
            ml
          </label>
          <Button variant="secondary" onClick={load} testId="markets-refresh-btn"><RefreshCcw size={13} /> Refresh</Button>
        </div>
      </div>
      {err && <div className="mb-3 text-xs text-red-600" data-testid="markets-error">{err}</div>}
      <div className="space-y-3">
        {rows.map((r) => {
          const available = val(r, "available");
          const rowDirty = !!dirty[r.marketId] && Object.keys(dirty[r.marketId]).length > 0;
          return (
            <div key={r.marketId} data-testid={`market-row-${r.marketId}`}
              className="border rounded p-3" style={{ borderColor: "var(--ph-border-subtle)", opacity: available ? 1 : 0.65 }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe size={14} className="text-slate-500" />
                  <span className="text-sm font-semibold text-slate-900">{r.marketName}</span>
                  <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded px-1.5 py-0.5"
                    data-testid={`market-currency-${r.marketId}`}>{r.currency}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!available}
                      onChange={(e) => { edit(r.marketId, "available", e.target.checked); saveRow(r.marketId, { available: e.target.checked }); }}
                      data-testid={`market-available-${r.marketId}`} />
                    Available in this market
                  </label>
                  <Button variant="primary" disabled={!rowDirty || saving === r.marketId}
                    onClick={() => saveRow(r.marketId)} testId={`market-save-${r.marketId}`}>
                    <Save size={13} /> {saving === r.marketId ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Price ({r.currency})</span>
                  <input type="number" min={0} value={val(r, "price") ?? ""} disabled={!available}
                    onChange={(e) => edit(r.marketId, "price", e.target.value === "" ? null : e.target.value)}
                    data-testid={`market-price-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Launch status</span>
                  <select value={val(r, "launchStatus") ?? "pre_launch"} disabled={!available}
                    onChange={(e) => edit(r.marketId, "launchStatus", e.target.value)}
                    data-testid={`market-launch-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded bg-white" style={{ borderColor: "var(--ph-border-strong)" }}>
                    {LAUNCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Stock (units)</span>
                  <input type="number" min={0} value={val(r, "stock") ?? ""} disabled={!available}
                    onChange={(e) => edit(r.marketId, "stock", e.target.value === "" ? null : e.target.value)}
                    data-testid={`market-stock-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Fulfillment</span>
                  <select value={val(r, "fulfillment")?.method ?? ""} disabled={!available}
                    onChange={(e) => edit(r.marketId, "fulfillment", { ...(val(r, "fulfillment") ?? {}), method: e.target.value || null })}
                    data-testid={`market-fulfillment-method-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded bg-white" style={{ borderColor: "var(--ph-border-strong)" }}>
                    {FULFILLMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Packaging / 100ml ({r.currency})</span>
                  <input type="number" min={0} value={val(r, "packagingCostPer100ml") ?? ""} disabled={!available}
                    onChange={(e) => edit(r.marketId, "packagingCostPer100ml", e.target.value === "" ? null : Number(e.target.value))}
                    data-testid={`market-packaging-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
                </label>
                <label className="flex flex-col gap-1 col-span-2 md:col-span-4">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Fulfillment notes</span>
                  <input value={val(r, "fulfillment")?.notes ?? ""} disabled={!available}
                    onChange={(e) => edit(r.marketId, "fulfillment", { ...(val(r, "fulfillment") ?? {}), notes: e.target.value })}
                    data-testid={`market-fulfillment-notes-${r.marketId}`}
                    className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
                </label>
              </div>
              {(() => {
                const c = cogs[r.marketId];
                if (!c) return null;
                return (
                  <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--ph-border-subtle)" }}
                    data-testid={`market-cogs-${r.marketId}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Calculator size={13} className="text-slate-500" />
                      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                        COGS per {c.bottleMl}ml bottle · {r.currency}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Aromatics: <b data-testid={`cogs-aromatics-${r.marketId}`}>{money(c.components.aromatics, c.currency)}</b></span>
                      <span>Alcohol: <b>{money(c.components.alcohol, c.currency)}</b></span>
                      <span>DPG: <b>{money(c.components.dpg, c.currency)}</b></span>
                      <span>Packaging: <b>{money(c.components.packaging, c.currency)}</b></span>
                      <span className="text-slate-900">Total COGS: <b data-testid={`cogs-total-${r.marketId}`}>{money(c.cogsPerBottle, c.currency)}</b></span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Retail: <b>{money(c.retailPrice, c.currency)}</b></span>
                      <span>Margin: <b data-testid={`margin-${r.marketId}`}>{money(c.margin, c.currency)}</b></span>
                      <span>Margin %: <b data-testid={`margin-pct-${r.marketId}`}>{c.marginPct === null ? "—" : `${c.marginPct}%`}</b></span>
                    </div>
                    {!c.complete && c.missing.length > 0 && (
                      <div className="text-[11px] text-amber-700 mt-1" data-testid={`cogs-missing-${r.marketId}`}>
                        Missing: {c.missing.join(" · ")}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
